import React, { useEffect } from 'react';
import { Drawer } from 'vaul';
import { useHistoryDismiss } from '@shared/hooks/useHistoryDismiss';
import { triggerHaptic } from '@shared/lib/haptics';
import { cn } from '@shared/lib/utils';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  snapPoints?: (string | number)[];
  children: React.ReactNode;
  className?: string;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  snapPoints,
  children,
  className,
}) => {
  useHistoryDismiss(open, () => {
    triggerHaptic(8);
    onOpenChange(false);
  });

  // Ensure focused inputs are centered within the sheet on mobile virtual keyboards
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        setTimeout(() => {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 300);
      }
    };

    window.addEventListener('focusin', handleFocusIn);
    return () => window.removeEventListener('focusin', handleFocusIn);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    triggerHaptic(8);
    onOpenChange(nextOpen);
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      snapPoints={snapPoints}
      shouldScaleBackground
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity" />
        <Drawer.Content
          data-app-chrome
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-2xl border-t border-border bg-card shadow-2xl pb-[calc(var(--safe-bottom)+1rem)] focus:outline-none',
            className
          )}
        >
          <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted-foreground/30 shrink-0" />

          {title ? (
            <div className="px-5 pt-3 pb-2 border-b border-border/40 shrink-0">
              <Drawer.Title className="text-base font-semibold tracking-tight text-foreground">
                {title}
              </Drawer.Title>
              {description && (
                <Drawer.Description className="text-xs text-muted-foreground mt-0.5">
                  {description}
                </Drawer.Description>
              )}
            </div>
          ) : (
            <Drawer.Title className="sr-only">Modal Sheet</Drawer.Title>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-4 overscroll-contain">
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
